import { GENRE_OPTIONS } from './constants';

/**
 * Convert a canonical genre name to a URL-friendly slug
 * "Mystery & Thriller" → "mystery-and-thriller"
 * "Young Adult (YA)" → "young-adult-ya"
 */
export function genreToSlug(genre: string): string {
  return genre
    .toLowerCase()
    .replace(/[&]/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Convert a URL slug back to a canonical genre name
 * "mystery-and-thriller" → "Mystery & Thriller"
 */
export function slugToGenre(slug: string): string | null {
  const normalizedSlug = slug.toLowerCase();
  
  for (const genre of GENRE_OPTIONS) {
    if (genreToSlug(genre) === normalizedSlug) {
      return genre;
    }
  }
  
  return null;
}

/**
 * Check if a string is a valid canonical genre
 */
export function isCanonicalGenre(genre: string): boolean {
  return (GENRE_OPTIONS as readonly string[]).includes(genre);
}

/**
 * Common mappings from raw Open Library subjects to canonical genres
 * Used for client-side matching when the database lookup wasn't done
 */
const RAW_TO_CANONICAL_MAP: Record<string, string> = {
  // Open Library subjects (lowercase)
  'fiction': 'Fiction (General / Contemporary)',
  "children's fiction": "Middle Grade & Children's",
  'juvenile fiction': "Middle Grade & Children's",
  'young adult fiction': 'Young Adult (YA)',
  'wizards': 'Fantasy',
  'magic': 'Fantasy',
  'dragons': 'Fantasy',
  'fantasy fiction': 'Fantasy',
  'fantasy': 'Fantasy',
  'science fiction': 'Science Fiction',
  'space': 'Science Fiction',
  'mystery and detective stories': 'Mystery & Thriller',
  'mystery': 'Mystery & Thriller',
  'detective and mystery stories': 'Mystery & Thriller',
  'suspense': 'Mystery & Thriller',
  'thrillers': 'Mystery & Thriller',
  'horror': 'Horror & Gothic',
  'gothic': 'Horror & Gothic',
  'romance': 'Romance',
  'love stories': 'Romance',
  'historical fiction': 'Historical Fiction',
  'history': 'History',
  'biography': 'Biography',
  'autobiography': 'Memoir & Autobiography',
  'memoir': 'Memoir & Autobiography',
  'poetry': 'Poetry',
  'essays': 'Essays & Journalism',
  'self-help': 'Self-Help & Personal Development',
  'psychology': 'Psychology & Mental Health',
  'philosophy': 'Philosophy',
  'true crime': 'True Crime',
  'crime': 'Crime & Detective',
  'comics': 'Graphic Novels & Comics',
  'graphic novels': 'Graphic Novels & Comics',
  'short stories': 'Short Stories',
  'classics': 'Classics',
  'literary fiction': 'Literary Fiction',
  'cooking': 'Food & Cooking',
  'travel': 'Travel & Adventure',
  'art': 'Art & Photography',
  'photography': 'Art & Photography',
  'business': 'Business & Economics',
  'economics': 'Business & Economics',
  'science': 'Science & Nature',
  'nature': 'Science & Nature',
  'politics': 'Politics & Current Affairs',
  'feminism': 'Gender Studies & Feminism',
  'lgbtq+': 'LGBTQ+ Studies',
  // Common variations
  'england, fiction': 'Fiction (General / Contemporary)',
  'schools': "Middle Grade & Children's",
  'boarding schools': "Middle Grade & Children's",
  'witches': 'Fantasy',
  'supernatural': 'Horror & Gothic',
  'adventure': 'Travel & Adventure',
  'adventure stories': 'Travel & Adventure',
  'detective': 'Crime & Detective',
  'psychological fiction': 'Literary Fiction',
};

/**
 * Try to map a raw genre/subject to a canonical genre
 * Returns the canonical genre if found, or null if no mapping exists
 */
export function mapToCanonicalGenre(rawGenre: string): string | null {
  // First check if it's already canonical
  if (isCanonicalGenre(rawGenre)) {
    return rawGenre;
  }
  
  // Try exact match in mapping table
  const normalized = rawGenre.toLowerCase().trim();
  if (RAW_TO_CANONICAL_MAP[normalized]) {
    return RAW_TO_CANONICAL_MAP[normalized];
  }
  
  // Try partial matching - if any key is contained in the genre
  for (const [key, canonical] of Object.entries(RAW_TO_CANONICAL_MAP)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return canonical;
    }
  }
  
  return null;
}

/**
 * Get genre metadata (descriptions, icons, etc.)
 */
export const GENRE_METADATA: Record<string, { description: string; icon?: string }> = {
  'Fiction (General / Contemporary)': {
    description: 'Modern stories reflecting everyday life and human experiences.',
  },
  'Literary Fiction': {
    description: 'Character-driven narratives with depth and artistic prose.',
  },
  'Classics': {
    description: 'Timeless works that have shaped literature across generations.',
  },
  'Romance': {
    description: 'Stories centered on love, relationships, and emotional journeys.',
  },
  'Fantasy': {
    description: 'Imaginative worlds with magic, mythical creatures, and epic adventures.',
  },
  'Science Fiction': {
    description: 'Explorations of technology, space, and speculative futures.',
  },
  'Mystery & Thriller': {
    description: 'Suspenseful stories with puzzles, investigations, and tension.',
  },
  'Crime & Detective': {
    description: 'Stories focused on crime-solving and investigative work.',
  },
  'Horror & Gothic': {
    description: 'Dark tales designed to frighten, unsettle, and thrill.',
  },
  'Historical Fiction': {
    description: 'Stories set in past eras, bringing history to life.',
  },
  'Young Adult (YA)': {
    description: 'Coming-of-age stories for teen and young adult readers.',
  },
  "Middle Grade & Children's": {
    description: 'Adventures and stories for younger readers.',
  },
  'Graphic Novels & Comics': {
    description: 'Visual storytelling through sequential art.',
  },
  'Short Stories': {
    description: 'Brief, focused narratives that pack a punch.',
  },
  'Memoir & Autobiography': {
    description: 'Personal accounts of real-life experiences and journeys.',
  },
  'Biography': {
    description: 'Stories of remarkable lives and achievements.',
  },
  'History': {
    description: 'Non-fiction explorations of past events and eras.',
  },
  'Politics & Current Affairs': {
    description: 'Analysis of political systems, governance, and current events.',
  },
  'Identity & Social Issues': {
    description: 'Explorations of identity, culture, and societal challenges.',
  },
  'Gender Studies & Feminism': {
    description: 'Critical analysis of gender, equality, and feminist thought.',
  },
  'LGBTQ+ Studies': {
    description: 'Stories and analysis centered on LGBTQ+ experiences.',
  },
  'True Crime': {
    description: 'Real-life crime investigations and criminal psychology.',
  },
  'Essays & Journalism': {
    description: 'Thoughtful commentary and investigative reporting.',
  },
  'Science & Nature': {
    description: 'Explorations of the natural world and scientific discovery.',
  },
  'Psychology & Mental Health': {
    description: 'Understanding the mind, behavior, and emotional wellbeing.',
  },
  'Philosophy': {
    description: 'Deep thinking about existence, ethics, and knowledge.',
  },
  'Self-Help & Personal Development': {
    description: 'Guidance for growth, improvement, and life skills.',
  },
  'Business & Economics': {
    description: 'Insights into commerce, finance, and economic systems.',
  },
  'Travel & Adventure': {
    description: 'Journeys to distant places and daring explorations.',
  },
  'Food & Cooking': {
    description: 'Culinary arts, recipes, and food culture.',
  },
  'Art & Photography': {
    description: 'Visual arts, artistic movements, and photographic work.',
  },
  'Poetry': {
    description: 'Expressive verse exploring emotions and ideas.',
  },
};

/**
 * Get the description for a genre
 */
export function getGenreDescription(genre: string): string {
  return GENRE_METADATA[genre]?.description || `Explore books in the ${genre} genre.`;
}
